import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useListasConfig } from './useListasConfig';
import { db } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc, doc, setDoc,
  orderBy, query, serverTimestamp, getDoc, where, deleteDoc
} from 'firebase/firestore';
import { useColeccion, useParchear, BarraActualizacion, useEstadoPersistente, useAlCambiar, usePosicionScroll, fechasATimestamp, useBorrador, useDescartarBorrador, useHayBorrador, useBorradorPendiente, AvisoBorrador } from './DatosContext';
import { BotonSolicitarEliminacion, AvisoSolicitudEliminacion } from './SolicitudEliminacion';
import DialogoConfirmar from './Dialogo';
import useTecladoLista, { ESTILO_ACTIVO } from './useTecladoLista';
import { ejecutarEliminacion } from './operaciones';

// Regla de METRON: norm() en los dos lados y String() porque los códigos importados son números
const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// ─── Vínculo presupuesto → proyecto (sesión 34) ──────────────────────────────
// La fuente de verdad de "este presupuesto ya tiene proyecto" es presupuesto.proyectoId (73 vínculos históricos).
// Al crear, editar o borrar un proyecto se mantiene aquí; antes no se escribía nunca y el botón "Crear proyecto"
// seguía apareciendo, lo que permitía duplicar proyectos.
async function vincularPresupuesto(presupuestoId, proyectoId, parchearPres) {
  if (!presupuestoId) return;
  try {
    await updateDoc(doc(db, 'presupuestos', presupuestoId), { proyectoId: proyectoId || null });
    parchearPres(prev => prev.map(p => p.id === presupuestoId ? { ...p, proyectoId: proyectoId || null } : p));
  } catch (e) { console.error('No se pudo actualizar el vínculo del presupuesto', presupuestoId, e); }
}

// Siguiente código libre del año en curso (formato AAXXX: 26130 tras 26129)
function siguienteCodigo(proyectos) {
  const aa = String(new Date().getFullYear()).slice(2);
  let max = 0;
  proyectos.forEach(p => {
    const c = String(p.codigo ?? '').trim();
    if (c.length === 5 && c.startsWith(aa) && /^\d+$/.test(c)) max = Math.max(max, parseInt(c.slice(2), 10));
  });
  return `${aa}${String(max + 1).padStart(3, '0')}`;
}

// ─── Lista reducida de proyectos (sesión 33) ─────────────────────────────────
// proyectos_lista/{id} guarda solo datos no económicos. Es lo único de proyectos que pueden leer los empleados
// (Control de Horas y su Dashboard). Se actualiza aquí cada vez que se crea, edita o borra un proyecto.
const CAMPOS_LISTA = ['codigo', 'nombre', 'estado', 'fecha_inicio', 'fecha_fin_obra', 'resenia'];
export async function sincronizarListaProyecto(id, datos) {
  try {
    if (datos === null) { await deleteDoc(doc(db, 'proyectos_lista', id)); return; }
    const d = {};
    CAMPOS_LISTA.forEach(k => { if (datos[k] !== undefined) d[k] = datos[k]; });
    if (Object.keys(d).length) await setDoc(doc(db, 'proyectos_lista', id), d, { merge: true });
  } catch (e) { console.error('No se pudo actualizar la lista reducida de proyectos', id, e); }
}


// ─── CONSTANTES ───────────────────────────────────────────────────────────────


const ESTADOS = [
  { valor: 'en_curso',   etiqueta: 'En curso',   color: '#534AB7' },
  { valor: 'entregado',  etiqueta: 'Entregado',  color: '#BA7517' },
  { valor: 'finalizado', etiqueta: 'Finalizado', color: '#1D9E75' },
  { valor: 'archivado',  etiqueta: 'Archivado',  color: '#4a4a6a' },
];

function estadoInfo(valor) {
  return ESTADOS.find(e => e.valor === valor) || ESTADOS[0];
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function fmt(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtInput(fecha) {
  if (!fecha) return '';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  return d.toISOString().split('T')[0];
}

function fmtEuros(v) {
  if (!v && v !== 0) return '—';
  // Sesión 34: separador de miles también en cifras de 4 dígitos (es-ES por defecto no lo pone: "3985 €")
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, useGrouping: 'always' }).format(v);
}
const fmtNum = (n) => n ? new Intl.NumberFormat('es-ES', { useGrouping: 'always' }).format(n) : '—';

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(v))}%`;
}

function colorMargen(pct) {
  if (pct === null || pct === undefined) return '#AFA9EC';
  if (pct >= 50) return '#1D9E75';
  if (pct >= 35) return '#BA7517';
  return '#D85A30';
}

function calcularMargen(proyecto) {
  const base = proyecto.estado === 'finalizado'
    ? (proyecto.importe_facturado || proyecto.importe_encargo || 0)
    : (proyecto.importe_encargo || 0);
  if (!base) return null;
  const coste = (proyecto.coste_personal || 0) + (proyecto.costes_produccion || 0);
  return ((base - coste) / base) * 100;
}

function calcularDias(fechaA, fechaB) {
  if (!fechaA || !fechaB) return null;
  const a = fechaA.toDate ? fechaA.toDate() : new Date(fechaA);
  const b = fechaB.toDate ? fechaB.toDate() : new Date(fechaB);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function estiloInput(error) {
  return {
    background: '#0e0b24',
    border: `1px solid ${error ? '#D85A30' : '#534AB7'}`,
    borderRadius: '8px', padding: '10px 14px',
    color: '#ffffff', fontSize: '0.9rem', width: '100%',
    outline: 'none', fontFamily: 'DM Sans, sans-serif',
    colorScheme: 'dark', boxSizing: 'border-box',
  };
}

function Campo({ label, children, error, fullWidth, style }) {
  return (
    <div data-error={error ? 'true' : undefined} style={{ gridColumn: fullWidth ? '1 / -1' : 'span 1', display: 'flex', flexDirection: 'column', gap: '6px', ...style }}>
      <label style={styles.label}>{label}</label>
      {children}
      {error && <span style={{ color: '#D85A30', fontSize: '0.78rem' }}>{error}</span>}
    </div>
  );
}

function EstadoBadge({ estado }) {
  const info = estadoInfo(estado);
  return (
    <span style={{ background: info.color, color: '#fff', fontSize: '0.72rem', fontWeight: '500', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
      {info.etiqueta}
    </span>
  );
}

function ModalConfirmar({ titulo, mensaje, onConfirmar, onCancelar, textoCancelar, textoConfirmar }) {
  // Sesión 34: todos los avisos usan el diálogo común (la opción segura es la destacada; Enter/Esc la eligen)
  const esBorrado = /eliminar|eliminando/i.test(textoConfirmar || '');
  return (
    <DialogoConfirmar
      titulo={titulo || 'Cambios sin guardar'}
      mensaje={mensaje}
      textoSeguro={textoCancelar && textoCancelar !== 'Cancelar' ? textoCancelar : (esBorrado ? 'No eliminar' : 'Seguir editando')}
      textoPeligroso={textoConfirmar || 'Salir sin guardar'}
      onSeguro={onCancelar}
      onPeligroso={onConfirmar}
      ocupado={/^eliminando/i.test(textoConfirmar || '')}
    />
  );
}

// ─── SELECTOR DE CLIENTE CON INLINE ───────────────────────────────────────────

function SelectorCliente({ clientes, clienteId, onChange, onClienteCreado, prefill, sugerido }) {
  const [busqueda, setBusqueda] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [formCliente, setFormCliente] = useState({ tipo: 'empresa', razon_social: '', nombre: '', apellidos: '', movil: '', email: '' });
  const [guardando, setGuardando] = useState(false);
  const [clienteNuevoAviso, setClienteNuevoAviso] = useState(false);

  const clienteSeleccionado = clientes.find(c => c.id === clienteId);

  const filtrados = clientes.filter(c => {
    const nombre = c.tipo === 'empresa' ? (c.razon_social || '') : `${c.nombre || ''} ${c.apellidos || ''}`;
    const t = norm(busqueda);
    return norm(nombre).includes(t) ||
      (c.c1_movil || '').includes(busqueda) ||
      (c.c1_telefono || '').includes(busqueda) ||
      (c.movil || '').includes(busqueda) ||
      (c.telefono || '').includes(busqueda);
  }).slice(0, 8);
  // Sesión 34: flechas, Enter y Esc
  const teclaCli = useTecladoLista(busqueda ? filtrados : [], c => { onChange(c.id); setBusqueda(''); }, () => setBusqueda(''));

  const crearCliente = async () => {
    const nombre = formCliente.tipo === 'empresa' ? formCliente.razon_social : formCliente.nombre;
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'clientes'), {
        ...formCliente,
        creado_en: serverTimestamp(),
      });
      // getDoc para obtener timestamps reales — REGLA PERMANENTE
      const snapCli = await getDoc(docRef);
      const nuevoCliente = { id: docRef.id, ...snapCli.data() };
      onClienteCreado(nuevoCliente);
      onChange(docRef.id);
      setMostrarForm(false);
      setClienteNuevoAviso(true);
      setBusqueda('');
    } catch (e) { console.error(e); }
    setGuardando(false);
  };

  if (clienteSeleccionado) {
    const nombre = clienteSeleccionado.tipo === 'empresa'
      ? clienteSeleccionado.razon_social
      : `${clienteSeleccionado.nombre || ''} ${clienteSeleccionado.apellidos || ''}`.trim();
    return (
      <div>
        <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid #1D9E75', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#ffffff', fontWeight: '500' }}>{nombre}</span>
          <button style={styles.botonEditar} onClick={() => { onChange(''); setClienteNuevoAviso(false); }}>Cambiar</button>
        </div>
        {sugerido && !clienteNuevoAviso && (
          <p style={{ color: '#AFA9EC', fontSize: '0.78rem', marginTop: '6px' }}>
            Propuesto a partir del lead del presupuesto. Cámbialo si se va a facturar a otra empresa.
          </p>
        )}
        {clienteNuevoAviso && (
          <div style={{ marginTop: '8px', background: 'rgba(186,117,23,0.12)', border: '1px solid rgba(186,117,23,0.4)', borderRadius: '8px', padding: '10px 14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
            <p style={{ color: '#BA7517', fontSize: '0.82rem', lineHeight: '1.5', margin: 0 }}>
              Cliente creado con datos básicos. Recuerda completar su ficha en el módulo de Clientes cuando termines con este proyecto.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {!mostrarForm ? (
        <>
          <input
            style={{ ...styles.buscador, width: '100%', boxSizing: 'border-box' }}
            placeholder="Buscar cliente por nombre o razón social..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            onKeyDown={teclaCli.onKeyDown}
          />
          {busqueda && (
            <div style={{ background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
              {filtrados.length === 0
                ? <p style={{ color: '#AFA9EC', padding: '12px 16px', fontSize: '0.85rem' }}>No se encontraron clientes</p>
                : filtrados.map((c, i) => {
                  const nombre = c.tipo === 'empresa' ? c.razon_social : `${c.nombre || ''} ${c.apellidos || ''}`.trim();
                  return (
                    <div key={c.id} style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(83,74,183,0.1)', ...(i === teclaCli.activo ? ESTILO_ACTIVO : {}) }}
                      onMouseEnter={() => teclaCli.setActivo(i)}
                      onClick={() => { onChange(c.id); setBusqueda(''); }}>
                      <span style={{ color: '#ffffff', fontSize: '0.88rem' }}>{nombre}</span>
                      <span style={{ color: '#AFA9EC', fontSize: '0.75rem', marginLeft: '8px' }}>{c.tipo === 'empresa' ? 'Empresa' : 'Particular'}</span>
                    </div>
                  );
                })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button style={{ ...styles.botonSecundario, fontSize: '0.82rem', padding: '7px 14px' }}
              onClick={() => { if (prefill) setFormCliente(f => ({ ...f, ...prefill })); setMostrarForm(true); }}>
              + Crear nuevo cliente
            </button>
            <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>
              {prefill ? 'Se rellenará con los datos del lead del presupuesto.' : 'Basta con el nombre; los datos fiscales se completan después en Clientes.'}
            </span>
          </div>
        </>
      ) : (
        <div style={{ background: '#0e0b24', borderRadius: '10px', padding: '16px', border: '1px solid rgba(83,74,183,0.3)' }}>
          <p style={{ color: '#7B6FE8', fontSize: '0.82rem', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nuevo cliente — datos básicos</p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {[{ v: 'empresa', l: 'Empresa' }, { v: 'particular', l: 'Particular' }].map(({ v, l }) => (
              <button key={v} onClick={() => setFormCliente(f => ({ ...f, tipo: v }))} style={{
                padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                borderColor: formCliente.tipo === v ? '#534AB7' : 'rgba(83,74,183,0.3)',
                background: formCliente.tipo === v ? '#534AB7' : 'transparent',
                color: formCliente.tipo === v ? '#fff' : '#AFA9EC',
                fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              }}>{l}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {formCliente.tipo === 'empresa' ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <input style={estiloInput()} placeholder="Razón social *" value={formCliente.razon_social}
                  onChange={e => setFormCliente(f => ({ ...f, razon_social: e.target.value }))} />
              </div>
            ) : (
              <>
                <input style={estiloInput()} placeholder="Nombre *" value={formCliente.nombre}
                  onChange={e => setFormCliente(f => ({ ...f, nombre: e.target.value }))} />
                <input style={estiloInput()} placeholder="Apellidos" value={formCliente.apellidos}
                  onChange={e => setFormCliente(f => ({ ...f, apellidos: e.target.value }))} />
              </>
            )}
            <input style={estiloInput()} placeholder="Móvil" value={formCliente.movil}
              onChange={e => setFormCliente(f => ({ ...f, movil: e.target.value }))} />
            <input style={estiloInput()} placeholder="Email" value={formCliente.email}
              onChange={e => setFormCliente(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button style={{ ...styles.botonSecundario, fontSize: '0.82rem', padding: '7px 14px' }} onClick={() => setMostrarForm(false)}>Cancelar</button>
            <button style={{ ...styles.botonPrimario, fontSize: '0.82rem', padding: '7px 14px' }} onClick={crearCliente} disabled={guardando}>
              {guardando ? 'Creando...' : 'Crear cliente'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FORMULARIO DE ALTA ───────────────────────────────────────────────────────

const FORM_INICIAL = () => ({
  codigo: '',
  nombre: '',
  clienteId: '',
  presupuestoId: '',
  sector: '',
  tipo_proyecto: '',
  fuente: '',
  num_encargo: '',
  superficie: '',
  direccion: '',
  cp: '',
  ciudad: '',
  provincia: '',
  pais: 'España',
  fecha_inicio: new Date().toISOString().split('T')[0],
  fecha_entrega_proyecto: '',
  fecha_fin_obra: '',
  importe_encargo: '',
  costes_produccion: '',
  importe_facturado: '',
  estado: 'en_curso',
  observaciones: '',
});

function FormularioProyecto({ inicial, titulo, onGuardar, onCancelar, esAdmin, cancelarDirecto, presupuestoIdInicial, proyectos = [], textoVolver = '← Proyectos', claveBorrador = null }) {
  const { sectores, fuentes, tiposProyecto } = useListasConfig();
  const [estadoInicialForm] = useState(() => inicial || FORM_INICIAL());
  // Sesión 34: borrador — lo escrito se conserva si sales a otro módulo sin guardar
  const [form, setForm, recuperado] = useBorrador(claveBorrador, estadoInicialForm);
  const [avisoBorrador, setAvisoBorrador] = useState(recuperado);
  const descartarBorrador = useDescartarBorrador();
  // Referencia para saber si hay cambios sin guardar (el código propuesto no cuenta como cambio)
  const [formBase] = useState(estadoInicialForm);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  // Sesión 32 · Entrega B: clientes, presupuestos y leads vienen del almacén compartido.
  // Antes este formulario descargaba las tres colecciones enteras (unas 1.500 lecturas) cada vez que se abría.
  const { items: clientes, setItems: setClientes } = useColeccion('clientes');
  const { items: presCrudos } = useColeccion('presupuestos');
  const { items: leadsAlmacen } = useColeccion('leads');
  const [busquedaPres, setBusquedaPres] = useState('');
  // Sesión 34: cliente propuesto o datos del lead para crear el cliente a partir del presupuesto elegido
  const [clienteSugerido, setClienteSugerido] = useState(false);
  const [prefillCliente, setPrefillCliente] = useState(null);

  // Sesión 34: solo se ofrecen presupuestos aceptados que todavía no tienen proyecto (salvo el de este proyecto).
  // Antes se podía elegir uno ya usado y duplicar el proyecto.
  const presupuestoPropio = inicial?.presupuestoId || '';
  const presupuestosAceptados = useMemo(() => {
    const leadsMap = {};
    leadsAlmacen.forEach(l => { leadsMap[l.id] = l; });
    const usadosPorProyecto = new Set(proyectos.filter(p => p.id !== inicial?.id && p.presupuestoId).map(p => p.presupuestoId));
    return presCrudos
      .filter(p => p.estado === 'aceptado')
      .filter(p => p.id === presupuestoPropio || (!p.proyectoId && !usadosPorProyecto.has(p.id)))
      .map(p => {
        const lead = leadsMap[p.leadId];
        return {
          ...p,
          leadNombre: lead ? `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() : (p.leadNombre || ''),
          leadEmpresa: lead?.empresa || p.leadEmpresa || '',
          leadMovil: lead?.movil || '',
          leadTelefono: lead?.telefono || '',
        };
      });
  }, [presCrudos, leadsAlmacen, proyectos, inicial?.id, presupuestoPropio]);

  // Sesión 34: código propuesto (siguiente libre del año) en las altas. Si se deja vacío, se usa este.
  const codigoSugerido = !inicial && proyectos.length ? siguienteCodigo(proyectos) : '';
  const codigoEfectivo = String(form.codigo ?? '').trim() || codigoSugerido;

  // Precargar desde presupuesto: datos del encargo, ubicación de la oportunidad y cliente del lead.
  // Solo se rellena lo que está vacío; todo queda editable.
  const seleccionarPresupuesto = async (pres) => {
    setBusquedaPres('');
    setForm(f => ({
      ...f,
      presupuestoId: pres.id,
      sector: pres.sector || f.sector,
      importe_encargo: pres.importe_encargo || pres.importe || f.importe_encargo,
      num_encargo: pres.num_encargo || f.num_encargo,
      superficie: pres.superficie || f.superficie,
    }));
    setErrores(e => ({ ...e, presupuestoId: undefined }));
    const lead = leadsAlmacen.find(l => l.id === pres.leadId);
    // Ubicación de la oportunidad: 1 lectura
    let op = null;
    if (pres.leadId && pres.oportunidadId) {
      try {
        const snap = await getDoc(doc(db, 'leads', pres.leadId, 'posibles_proyectos', pres.oportunidadId));
        if (snap.exists()) op = snap.data();
      } catch (e) { console.error('No se pudo leer la oportunidad del presupuesto', e); }
    }
    setForm(f => ({
      ...f,
      fuente: f.fuente || lead?.fuente || '',
      direccion: f.direccion || op?.direccion || '',
      cp: f.cp || op?.cp || '',
      ciudad: f.ciudad || op?.ciudad || '',
      provincia: f.provincia || op?.provincia || '',
      pais: (f.pais && f.pais !== 'España') ? f.pais : (op?.pais || f.pais || 'España'),
    }));
    // Cliente: el que ya esté ligado a ese lead (por leadId o por su COD_LEAD); si no hay, datos del lead para crearlo
    const codLead = op?.cod_lead ?? null;
    const existente = clientes.find(c => (c.leadId && c.leadId === pres.leadId) || (codLead != null && c.cod_lead != null && String(c.cod_lead) === String(codLead)));
    if (existente) {
      setForm(f => (f.clienteId ? f : { ...f, clienteId: existente.id }));
      setClienteSugerido(true);
      setPrefillCliente(null);
    } else if (lead) {
      setClienteSugerido(false);
      setPrefillCliente({
        tipo: lead.empresa ? 'empresa' : 'particular',
        razon_social: lead.empresa || '',
        nombre: lead.nombre || '',
        apellidos: lead.apellidos || '',
        movil: lead.movil || lead.telefono || '',
        email: lead.email || '',
        leadId: lead.id,
      });
    }
  };

  // Si venimos desde un presupuesto aceptado, preseleccionarlo automáticamente (una sola vez)
  const preseleccionado = useRef(false);
  useEffect(() => {
    if (!presupuestoIdInicial || preseleccionado.current) return;
    const pres = presupuestosAceptados.find(p => p.id === presupuestoIdInicial);
    if (!pres) return;
    preseleccionado.current = true;
    seleccionarPresupuesto(pres);
  }, [presupuestosAceptados, presupuestoIdInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (campo, valor) => {
    setForm(f => {
      const nuevo = { ...f, [campo]: valor };
      // Al cambiar estado, rellenar fecha automáticamente si está vacía
      if (campo === 'estado') {
        const hoy = new Date().toISOString().split('T')[0];
        if (valor === 'entregado' && !f.fecha_entrega_proyecto) {
          nuevo.fecha_entrega_proyecto = hoy;
        }
        if (valor === 'finalizado') {
          if (!f.fecha_entrega_proyecto) nuevo.fecha_entrega_proyecto = hoy;
          if (!f.fecha_fin_obra) nuevo.fecha_fin_obra = hoy;
        }
      }
      return nuevo;
    });
    setErrores(e => ({ ...e, [campo]: undefined }));
  };

  const hayDatos = JSON.stringify(form) !== JSON.stringify(formBase);
  const intentarCancelar = () => {
    if (cancelarDirecto) {
      // Modo edición desde ficha: el modal lo gestiona la ficha, no el formulario
      onCancelar();
    } else if (hayDatos) {
      setModal(true);
    } else {
      onCancelar();
    }
  };

  // Cálculos de duración en tiempo real
  const duracionProyecto = form.fecha_inicio && form.fecha_entrega_proyecto
    ? calcularDias(new Date(form.fecha_inicio), new Date(form.fecha_entrega_proyecto)) : null;
  const duracionObra = form.fecha_entrega_proyecto && form.fecha_fin_obra
    ? calcularDias(new Date(form.fecha_entrega_proyecto), new Date(form.fecha_fin_obra)) : null;
  const velocidad = duracionProyecto && form.superficie
    ? (duracionProyecto / parseFloat(form.superficie)).toFixed(2) : null;

  const presFiltrados = presupuestosAceptados.filter(p => {
    const t = norm(busquedaPres);
    return !t ||
      norm(p.num_presupuesto).includes(t) ||
      norm(p.leadNombre).includes(t) ||
      norm(p.leadEmpresa).includes(t) ||
      (p.leadMovil || '').includes(busquedaPres) ||
      (p.leadTelefono || '').includes(busquedaPres) ||
      norm(p.sector).includes(t) ||
      norm(p.oportunidad_ciudad).includes(t);
  }).slice(0, 8);

  const presSeleccionado = presCrudos.find(p => p.id === form.presupuestoId);
  const teclaPres = useTecladoLista(busquedaPres ? presFiltrados : [], p => seleccionarPresupuesto(p), () => setBusquedaPres(''));

  const codigoRepetido = (cod) => {
    const c = String(cod ?? '').trim();
    return !!c && proyectos.some(p => p.id !== inicial?.id && String(p.codigo ?? '').trim() === c);
  };

  const validar = () => {
    const e = {};
    if (!codigoEfectivo) e.codigo = 'El código es obligatorio';
    else if (codigoRepetido(codigoEfectivo)) e.codigo = 'Ya existe un proyecto con este código';
    if (!String(form.nombre || '').trim()) e.nombre = 'El nombre es obligatorio';
    if (!form.clienteId) e.clienteId = 'El cliente es obligatorio. Si aún no sabes a qué empresa se factura, créalo solo con el nombre y complétalo después';
    if (!form.fecha_inicio) e.fecha_inicio = 'La fecha de inicio es obligatoria';
    if ((form.estado === 'entregado' || form.estado === 'finalizado') && !form.fecha_entrega_proyecto)
      e.fecha_entrega_proyecto = 'Obligatoria para estado Entregado o Finalizado';
    if (form.estado === 'finalizado' && !form.fecha_fin_obra)
      e.fecha_fin_obra = 'Obligatoria para estado Finalizado';
    return e;
  };

  const guardar = async () => {
    const e = validar();
    if (Object.keys(e).length > 0) {
      setErrores(e);
      // Scroll al primer error
      setTimeout(() => {
        const primerError = document.querySelector('[data-error="true"]');
        if (primerError) primerError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setGuardando(true);
    try {
      const datos = {
        ...form,
        codigo: inicial ? form.codigo : codigoEfectivo,
        fecha_inicio: form.fecha_inicio ? new Date(form.fecha_inicio) : null,
        fecha_entrega_proyecto: form.fecha_entrega_proyecto ? new Date(form.fecha_entrega_proyecto) : null,
        fecha_fin_obra: form.fecha_fin_obra ? new Date(form.fecha_fin_obra) : null,
        importe_encargo: parseFloat(form.importe_encargo) || null,
        costes_produccion: parseFloat(form.costes_produccion) || null,
        importe_facturado: parseFloat(form.importe_facturado) || null,
        superficie: parseFloat(form.superficie) || null,
      };
      await onGuardar(datos);
      descartarBorrador(claveBorrador);
    } catch (e) { console.error(e); setGuardando(false); }
  };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && <ModalConfirmar mensaje="¿Quieres salir sin guardar este proyecto?" onConfirmar={() => { descartarBorrador(claveBorrador); onCancelar(); }} onCancelar={() => setModal(false)} />}

      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={intentarCancelar}>{textoVolver}</button>
          <h1 style={styles.titulo}>{titulo}</h1>
        </div>
      </div>

      {avisoBorrador && (
        <AvisoBorrador onDescartar={() => { setForm(estadoInicialForm); setAvisoBorrador(false); }} />
      )}

      {/* IDENTIFICACIÓN */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Identificación del proyecto</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '160px 1fr', gap: '12px' }}>
            <Campo label="Código * (AAXXX)" error={errores.codigo || (codigoRepetido(form.codigo) ? 'Ya existe un proyecto con este código' : undefined)}>
              <input style={estiloInput(errores.codigo || codigoRepetido(form.codigo))} value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder={codigoSugerido || '26130'} />
              {!inicial && !String(form.codigo ?? '').trim() && codigoSugerido && <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>Si lo dejas vacío se usará {codigoSugerido}</span>}
            </Campo>
            <Campo label="Nombre del proyecto *" error={errores.nombre}>
              <input style={estiloInput(errores.nombre)} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre descriptivo del proyecto" />
            </Campo>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
            <Campo label="Sector">
              <select style={estiloInput()} value={form.sector} onChange={e => set('sector', e.target.value)}>
                <option value="">Sin sector</option>
                {sectores.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Campo>
            <Campo label="Tipo de proyecto">
              <select style={estiloInput()} value={form.tipo_proyecto} onChange={e => set('tipo_proyecto', e.target.value)}>
                <option value="">Sin tipo</option>
                {tiposProyecto.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Campo>
            <Campo label="Fuente">
              <select style={estiloInput()} value={form.fuente} onChange={e => set('fuente', e.target.value)}>
                <option value="">Sin fuente</option>
                {fuentes.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Campo>
          </div>
          <Campo label="Observaciones" style={{ gridColumn: '1 / -1' }}>
            <textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '72px' }} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Descripción o notas iniciales del proyecto..." />
          </Campo>
        </div>
      </div>

      {/* PRESUPUESTO VINCULADO */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Presupuesto aceptado <span style={{ color: '#AFA9EC', fontWeight: '400', textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>(opcional · rellena encargo, ubicación y cliente)</span></h2>
        {presSeleccionado ? (
          <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid #1D9E75', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ color: '#ffffff', fontWeight: '500' }}>{presSeleccionado.num_presupuesto}</span>
              <span style={{ color: '#AFA9EC', fontSize: '0.82rem', marginLeft: '8px' }}>{presSeleccionado.sector} · {fmtEuros(presSeleccionado.importe_encargo || presSeleccionado.importe)}</span>
            </div>
            <button style={styles.botonEditar} onClick={() => { set('presupuestoId', ''); setPrefillCliente(null); setClienteSugerido(false); }}>Quitar</button>
          </div>
        ) : (
          <>
            <input style={{ ...styles.buscador, width: '100%', boxSizing: 'border-box' }} placeholder="Buscar presupuesto aceptado sin proyecto (número, lead, empresa...)" value={busquedaPres} onChange={e => setBusquedaPres(e.target.value)} onKeyDown={teclaPres.onKeyDown} />
            {busquedaPres && (
              <div style={{ background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                {presFiltrados.length === 0
                  ? <p style={{ color: '#AFA9EC', padding: '12px 16px', fontSize: '0.85rem' }}>No hay presupuestos aceptados sin proyecto que coincidan</p>
                  : presFiltrados.map((p, i) => (
                    <div key={p.id} style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(83,74,183,0.1)', ...(i === teclaPres.activo ? ESTILO_ACTIVO : {}) }}
                      onMouseEnter={() => teclaPres.setActivo(i)}
                      onClick={() => seleccionarPresupuesto(p)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#ffffff', fontSize: '0.88rem', fontWeight: '500' }}>{p.num_presupuesto}</span>
                        <span style={{ color: '#1D9E75', fontSize: '0.78rem' }}>{fmtEuros(p.importe_encargo || p.importe)}</span>
                      </div>
                      <div style={{ marginTop: '2px' }}>
                        {p.leadNombre && <span style={{ color: '#7B6FE8', fontSize: '0.78rem' }}>{p.leadNombre}</span>}
                        {p.leadEmpresa && <span style={{ color: '#AFA9EC', fontSize: '0.75rem', marginLeft: '6px' }}>{p.leadEmpresa}</span>}
                        {p.sector && <span style={{ color: '#AFA9EC', fontSize: '0.75rem', marginLeft: '6px' }}>· {p.sector}</span>}
                        {p.oportunidad_ciudad && <span style={{ color: '#AFA9EC', fontSize: '0.75rem', marginLeft: '6px' }}>· {p.oportunidad_ciudad}</span>}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* CLIENTE */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Cliente *</h2>
        {errores.clienteId && <p style={{ color: '#D85A30', fontSize: '0.82rem', marginBottom: '8px' }}>{errores.clienteId}</p>}
        <SelectorCliente
          clientes={clientes}
          clienteId={form.clienteId}
          onChange={id => { set('clienteId', id); setClienteSugerido(false); }}
          onClienteCreado={nuevo => setClientes(prev => [nuevo, ...prev])}
          prefill={prefillCliente}
          sugerido={clienteSugerido}
        />
      </div>

      {/* ENCARGO */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Datos del encargo</h2>
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
          <Campo label="Nº Encargo">
            <input style={estiloInput()} value={form.num_encargo} onChange={e => set('num_encargo', e.target.value)} placeholder="26IE260317-391" />
          </Campo>
          <Campo label="Importe encargo (€)">
            <input type="number" style={estiloInput()} value={form.importe_encargo} onChange={e => set('importe_encargo', e.target.value)} placeholder="0" />
          </Campo>
          <Campo label="Superficie (m²)">
            <input type="number" style={estiloInput()} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" />
          </Campo>
        </div>
      </div>

      {/* COSTES Y FACTURACIÓN — solo administrador (sesión 34). Antes los costes estaban en "Datos del encargo"
          y el facturado solo aparecía con el proyecto finalizado. Las fórmulas se revisan en la auditoría de cálculos. */}
      {esAdmin && (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Costes y facturación</h2>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
            <Campo label="Coste de personal">
              <div style={{ ...estiloInput(), color: '#AFA9EC', border: '1px dashed rgba(83,74,183,0.5)' }}>Se calcula con las horas registradas</div>
            </Campo>
            <Campo label="Otros costes de producción (€)">
              <input type="number" style={estiloInput()} value={form.costes_produccion} onChange={e => set('costes_produccion', e.target.value)} placeholder="0" />
            </Campo>
            <Campo label="Importe facturado (€)">
              <input type="number" style={estiloInput()} value={form.importe_facturado} onChange={e => set('importe_facturado', e.target.value)} placeholder="0" />
            </Campo>
          </div>
          <p style={{ color: '#AFA9EC', fontSize: '0.75rem', marginTop: '10px' }}>Facturado hasta hoy, en cualquier estado del proyecto (facturación por fases).</p>
        </div>
      )}

      {/* UBICACIÓN */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Ubicación del proyecto</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Campo label="Dirección">
            <input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr 1fr' : '100px 1fr 1fr 1fr', gap: '12px' }}>
            <Campo label="CP"><input style={estiloInput()} value={form.cp} onChange={e => set('cp', e.target.value)} /></Campo>
            <Campo label="Ciudad"><input style={estiloInput()} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} /></Campo>
            <Campo label="Provincia"><input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} /></Campo>
            <Campo label="País"><input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} /></Campo>
          </div>
        </div>
      </div>

      {/* FECHAS */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Fechas</h2>
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <Campo label="Fecha de inicio *" error={errores.fecha_inicio}>
            <input type="date" style={estiloInput(errores.fecha_inicio)} value={form.fecha_inicio} onChange={e => set('fecha_inicio', e.target.value)} />
          </Campo>
          <Campo label="Entrega del proyecto (intelectual)" error={errores.fecha_entrega_proyecto}>
            <input type="date" style={estiloInput(errores.fecha_entrega_proyecto)} value={form.fecha_entrega_proyecto} onChange={e => set('fecha_entrega_proyecto', e.target.value)} />
          </Campo>
          <Campo label="Fin de obra" error={errores.fecha_fin_obra}>
            <input type="date" style={estiloInput(errores.fecha_fin_obra)} value={form.fecha_fin_obra} onChange={e => set('fecha_fin_obra', e.target.value)} />
          </Campo>
        </div>
        {(duracionProyecto !== null || duracionObra !== null) && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {duracionProyecto !== null && (
              <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 16px', border: '1px solid rgba(83,74,183,0.2)' }}>
                <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>DURACIÓN PROYECTO</p>
                <p style={{ color: '#7B6FE8', fontSize: '1rem', fontWeight: '500' }}>{duracionProyecto} días</p>
              </div>
            )}
            {duracionObra !== null && (
              <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 16px', border: '1px solid rgba(83,74,183,0.2)' }}>
                <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>DURACIÓN OBRA</p>
                <p style={{ color: '#7B6FE8', fontSize: '1rem', fontWeight: '500' }}>{duracionObra} días</p>
              </div>
            )}
            {velocidad !== null && (
              <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 16px', border: '1px solid rgba(83,74,183,0.2)' }}>
                <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>VELOCIDAD</p>
                <p style={{ color: '#7B6FE8', fontSize: '1rem', fontWeight: '500' }}>{velocidad} días/m²</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ESTADO */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Estado</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {ESTADOS.map(e => (
            <button key={e.valor} onClick={() => set('estado', e.valor)} style={{
              padding: '8px 20px', borderRadius: '20px', border: '1px solid',
              borderColor: form.estado === e.valor ? e.color : 'rgba(83,74,183,0.3)',
              background: form.estado === e.valor ? e.color : 'transparent',
              color: form.estado === e.valor ? '#fff' : '#AFA9EC',
              cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
            }}>{e.etiqueta}</button>
          ))}
        </div>
      </div>

      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar proyecto'}
        </button>
      </div>
    </div>
  );
}

// ─── FICHA DE PROYECTO ────────────────────────────────────────────────────────

function FichaProyecto({ proyecto, onVolver, onActualizar, esAdmin, verDatosEconomicos = true, onEliminar, proyectos = [] }) {
  const parchearPresupuestos = useParchear('presupuestos');
  // Sesión 34: si quedó un borrador sin guardar de esta ficha, se reabre en edición
  const hayBorrador = useHayBorrador(`proyecto:${proyecto.id}`);
  const descartarBorrador = useDescartarBorrador();
  const [modoEdicion, setModoEdicion] = useState(hayBorrador);
  const [datos, setDatos] = useState(proyecto);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [horas, setHoras] = useState([]);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [modal, setModal] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [tarifasMap, setTarifasMap] = useState({});
  const [nombresMap, setNombresMap] = useState({});

  useEffect(() => { cargarDatos(); }, [proyecto.id]);

  const cargarDatos = async () => {
    try {
      // Sesión 33: horas, usuarios y tarifas solo para quien ve datos económicos (administrador).
      // El comercial no tiene permiso para leerlos; antes la ficha fallaba entera y no cargaba ni las notas.
      const vacio = Promise.resolve({ docs: [] });
      const [notasSnap, horasSnap, usuariosSnap] = await Promise.all([
        getDocs(query(collection(db, 'proyectos', proyecto.id, 'notas'), orderBy('fecha', 'desc'))),
        verDatosEconomicos ? getDocs(query(collection(db, 'registros_horas'), where('proyectoId', '==', proyecto.id))) : vacio,
        verDatosEconomicos ? getDocs(collection(db, 'usuarios')) : vacio,
      ]);
      setNotas(notasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const horasProyecto = horasSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(h => !h.esInterno);
      setHoras(horasProyecto);

      // Mapa id → nombre para resolución de nombres
      const nMap = {};
      const usuariosData = usuariosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      usuariosData.forEach(u => { nMap[u.id] = u.nombre || u.email || u.id; });
      setNombresMap(nMap);

      // Cargar tarifas para calcular coste real por hora en cada registro
      const tMap = {};
      await Promise.all(usuariosData.map(async u => {
        try {
          const snap = await getDocs(collection(db, 'usuarios', u.id, 'tarifas'));
          if (snap.empty) { tMap[u.id] = []; return; }
          tMap[u.id] = snap.docs.map(d => d.data()).sort((a, b) => (b.fecha_inicio || '').localeCompare(a.fecha_inicio || ''));
        } catch { tMap[u.id] = []; }
      }));
      setTarifasMap(tMap);

      if (proyecto.clienteId) {
        const cliDoc = await getDoc(doc(db, 'clientes', proyecto.clienteId));
        if (cliDoc.exists()) setClienteInfo({ id: cliDoc.id, ...cliDoc.data() });
      }
    } catch (e) { console.error(e); }
  };

  const añadirNota = async () => {
    if (!nuevaNota.trim()) return;
    setGuardandoNota(true);
    try {
      await addDoc(collection(db, 'proyectos', proyecto.id, 'notas'), { texto: nuevaNota.trim(), fecha: serverTimestamp() });
      setNuevaNota('');
      const snap = await getDocs(query(collection(db, 'proyectos', proyecto.id, 'notas'), orderBy('fecha', 'desc')));
      setNotas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setGuardandoNota(false);
  };

  const toggleResenia = async () => {
    if (!esAdmin) return;
    const nuevoValor = !datos.resenia;
    await updateDoc(doc(db, 'proyectos', proyecto.id), { resenia: nuevoValor });
    sincronizarListaProyecto(proyecto.id, { resenia: nuevoValor });
    setDatos(d => ({ ...d, resenia: nuevoValor }));
    onActualizar({ ...datos, resenia: nuevoValor });
  };

  const handleGuardarEdicion = async (form) => {
    const actualizacion = { ...form, actualizado_en: serverTimestamp() };
    // Si se está archivando, guardar el estado previo para poder revertir en V1
    if (form.estado === 'archivado' && datos.estado !== 'archivado') {
      actualizacion.estado_previo_archivo = datos.estado;
    }
    // Si se está desarchivando, limpiar el estado previo
    if (form.estado !== 'archivado' && datos.estado === 'archivado') {
      actualizacion.estado_previo_archivo = null;
    }
    await updateDoc(doc(db, 'proyectos', proyecto.id), actualizacion);
    sincronizarListaProyecto(proyecto.id, form);
    // Sesión 34: si cambia el presupuesto del proyecto, se libera el anterior y se vincula el nuevo
    if ((form.presupuestoId || '') !== (datos.presupuestoId || '')) {
      if (datos.presupuestoId) await vincularPresupuesto(datos.presupuestoId, null, parchearPresupuestos);
      if (form.presupuestoId) await vincularPresupuesto(form.presupuestoId, proyecto.id, parchearPresupuestos);
    }
    const actualizado = { ...datos, ...form };
    setDatos(actualizado);
    onActualizar(actualizado);
    setModoEdicion(false);
  };

  const eliminarProyecto = async () => {
    setEliminando(true);
    try {
      // Sesión 34: borrado común (operaciones.js): notas, proyecto, lista reducida y presupuestos que quedan libres
      const r = await ejecutarEliminacion('proyecto', proyecto.id, datos);
      const libres = new Set(r.presupuestosLiberados || []);
      parchearPresupuestos(prev => prev.map(p => libres.has(p.id) ? { ...p, proyectoId: null } : p));
      onEliminar(proyecto.id);
    } catch (e) { console.error(e); setEliminando(false); }
  };

  // Sesión 34: solicitud de eliminación (comercial) o su rechazo (admin)
  const cambiarSolicitud = (solicitud) => {
    const actualizado = { ...datos, solicitud_eliminacion: solicitud };
    setDatos(actualizado);
    onActualizar(actualizado);
  };

  // Cálculos
  const esEstimado = datos.estado === 'en_curso' || datos.estado === 'entregado';
  const baseCalculo = esEstimado ? (datos.importe_encargo || 0) : (datos.importe_facturado || datos.importe_encargo || 0);

  const durProyecto = calcularDias(datos.fecha_inicio, datos.fecha_entrega_proyecto);
  const durObra = calcularDias(datos.fecha_entrega_proyecto, datos.fecha_fin_obra);
  const velProyecto = durProyecto && datos.superficie ? (durProyecto / datos.superficie).toFixed(2) : null;

  // Horas por empleado — nombre desde usuarios, coste desde tarifas vigentes en fecha del registro
  const horasPorEmpleado = horas.reduce((acc, h) => {
    const uid = h.empleadoId;
    const nombre = nombresMap[uid] || uid || 'Desconocido';
    if (!acc[nombre]) acc[nombre] = { horas: 0, coste: 0, uid };

    const fechaReg = typeof h.fecha === 'string' ? h.fecha
      : (h.fecha?.toDate ? h.fecha.toDate().toISOString().slice(0, 10) : null);
    const tarifas = tarifasMap[uid] || [];
    const tarifa = tarifas.find(t => {
      const desde = t.fecha_inicio || '';
      const hasta = t.fecha_fin || '9999-12-31';
      return fechaReg && fechaReg >= desde && fechaReg < hasta;
    }) || tarifas[0];
    const costeHora = tarifa?.coste_hora || 0;
    if (!costeHora) acc[nombre].sinTarifa = (acc[nombre].sinTarifa || 0) + (h.horas || 0);

    acc[nombre].horas += h.horas || 0;
    acc[nombre].coste += (h.horas || 0) * costeHora;
    return acc;
  }, {});
  const totalHoras = horas.reduce((s, h) => s + (h.horas || 0), 0);
  // Sesión 34: el coste de personal de la ficha es la suma de la tabla "Horas por empleado" (horas × tarifa vigente
  // en la fecha de cada apunte). Antes se leía un campo coste_personal que no tiene ningún proyecto y salía 0 €.
  // Listados, Dashboard y Análisis siguen sin descontarlo: se unifica en la auditoría de cálculos.
  const costePersonal = Object.values(horasPorEmpleado).reduce((s, d) => s + d.coste, 0);
  const horasSinTarifa = Object.values(horasPorEmpleado).reduce((s, d) => s + (d.sinTarifa || 0), 0);
  const costesProduccion = datos.costes_produccion || 0;
  const costeTotal = costePersonal + costesProduccion;
  const resultado = baseCalculo - costeTotal;
  const margen = baseCalculo ? (resultado / baseCalculo) * 100 : null;
  // Aviso al borrar (sesión 34): el admin tiene las horas cargadas; si no, se usa horas_total
  const horasApuntadas = totalHoras || datos.horas_total || 0;
  const personasConHoras = Object.keys(horasPorEmpleado).length;
  const fmtHorasTxt = (n) => `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2, useGrouping: 'always' }).format(n)} h`;
  const precioHoraVendida = totalHoras > 0 ? (baseCalculo / totalHoras).toFixed(2) : null;

  const nombreCliente = clienteInfo
    ? (clienteInfo.tipo === 'empresa' ? clienteInfo.razon_social : `${clienteInfo.nombre || ''} ${clienteInfo.apellidos || ''}`.trim())
    : datos.clienteId || '—';

  if (modoEdicion) {
    return (
      <>
        {modal && <ModalConfirmar mensaje="¿Salir sin guardar los cambios?" onConfirmar={() => { descartarBorrador(`proyecto:${proyecto.id}`); setModoEdicion(false); setModal(false); }} onCancelar={() => setModal(false)} />}
        <FormularioProyecto
          titulo="Editar proyecto"
          inicial={{ ...datos, fecha_inicio: fmtInput(datos.fecha_inicio), fecha_entrega_proyecto: fmtInput(datos.fecha_entrega_proyecto), fecha_fin_obra: fmtInput(datos.fecha_fin_obra), importe_encargo: datos.importe_encargo || '', costes_produccion: datos.costes_produccion || '', importe_facturado: datos.importe_facturado || '', superficie: datos.superficie || '' }}
          onGuardar={handleGuardarEdicion}
          onCancelar={() => setModal(true)}
          esAdmin={esAdmin}
          cancelarDirecto={true}
          proyectos={proyectos}
          claveBorrador={`proyecto:${proyecto.id}`}
        />
      </>
    );
  }

  return (
    <div style={styles.container}>
      {modalEliminar && (
        <ModalConfirmar
          titulo="Eliminar proyecto"
          mensaje={`¿Seguro que quieres eliminar el proyecto ${datos.codigo} — ${datos.nombre}? Se eliminarán también sus notas.${horasApuntadas > 0 ? ` ATENCIÓN: tiene ${fmtHorasTxt(horasApuntadas)} apuntadas${personasConHoras > 0 ? ` por ${personasConHoras} ${personasConHoras === 1 ? 'persona' : 'personas'}` : ''}; esos registros se quedarán sin proyecto.` : ''} Esta acción no se puede deshacer.`}
          textoCancelar="Cancelar"
          textoConfirmar={eliminando ? 'Eliminando...' : 'Sí, eliminar'}
          onConfirmar={eliminarProyecto}
          onCancelar={() => setModalEliminar(false)}
        />
      )}
      {/* Cabecera */}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button style={styles.botonVolver} onClick={onVolver}>← Proyectos</button>
          <h1 style={styles.titulo}>{datos.codigo} — {datos.nombre}</h1>
          <EstadoBadge estado={datos.estado} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {esAdmin
            ? <button style={styles.botonEliminar} onClick={() => setModalEliminar(true)}>Eliminar proyecto</button>
            : <BotonSolicitarEliminacion coleccion="proyectos" docId={proyecto.id} solicitud={datos.solicitud_eliminacion} nombreRegistro={`${datos.codigo} — ${datos.nombre}`} onCambio={cambiarSolicitud} />}
          <button style={styles.botonSecundario} onClick={() => setModoEdicion(true)}>Editar</button>
        </div>
      </div>
      <AvisoSolicitudEliminacion coleccion="proyectos" docId={proyecto.id} solicitud={datos.solicitud_eliminacion} esAdmin={esAdmin}
        onCambio={cambiarSolicitud} onEliminar={() => setModalEliminar(true)} />

      {/* Info secundaria */}
      <div style={{ padding: '24px 32px 32px' }}>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {datos.sector && <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sector: <span style={{ color: '#fff' }}>{datos.sector}</span></span>}
        {datos.tipo_proyecto && <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Tipo: <span style={{ color: '#fff' }}>{datos.tipo_proyecto}</span></span>}
        {datos.num_encargo && <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Encargo: <span style={{ color: '#fff' }}>{datos.num_encargo}</span></span>}
        {datos.fuente && <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Fuente: <span style={{ color: '#fff' }}>{datos.fuente}</span></span>}
      </div>

      {/* KPIs principales — solo admin ve datos económicos */}
      {verDatosEconomicos && (
        <div style={{ ...styles.gridKpis, gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '24px' }}>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Importe encargo</p>
            <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: '#7B6FE8' }}>{fmtEuros(datos.importe_encargo)}</p>
          </div>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Coste total</p>
            <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: '#AFA9EC' }}>{fmtEuros(costeTotal) !== '—' ? fmtEuros(costeTotal) : '—'}</p>
          </div>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Resultado {esEstimado && <span style={{ color: '#BA7517', fontSize: '0.7rem', marginLeft: '4px' }}>est.</span>}</p>
            <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: resultado >= 0 ? '#1D9E75' : '#D85A30' }}>{baseCalculo ? fmtEuros(resultado) : '—'}</p>
          </div>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Margen {esEstimado && <span style={{ color: '#BA7517', fontSize: '0.7rem', marginLeft: '4px' }}>est.</span>}</p>
            <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: colorMargen(margen) }}>{fmtPct(margen)}</p>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Columna izquierda */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Datos del proyecto */}
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Datos del proyecto</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                ['Cliente', nombreCliente],
                ['Sector', datos.sector || '—'],
                ['Tipo', datos.tipo_proyecto || '—'],
                ['Fuente', datos.fuente || '—'],
                ['Superficie', datos.superficie ? `${fmtNum(datos.superficie)} m\u00b2` : '—'],
                // Sesión 34: el comercial ve el importe de la venta (el admin lo ve en los KPIs de arriba)
                ...(!verDatosEconomicos ? [['Importe del encargo', fmtEuros(datos.importe_encargo)]] : []),
                ['Ubicación', [datos.ciudad, datos.provincia, datos.pais].filter(Boolean).join(', ') || '—'],
              ].map(([label, valor]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '4px 0', borderBottom: '1px solid rgba(83,74,183,0.08)' }}>
                  <span style={{ color: '#AFA9EC', fontSize: '0.82rem', flexShrink: 0 }}>{label}</span>
                  <span style={{ color: '#fff', fontSize: '0.88rem', textAlign: 'right' }}>{valor}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fechas y tiempos */}
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Fechas y tiempos</h2>
            {/* Línea de tiempo visual */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#534AB7', margin: '0 auto 4px' }} />
                <p style={{ color: '#AFA9EC', fontSize: '0.7rem' }}>Inicio</p>
                <p style={{ color: '#fff', fontSize: '0.78rem' }}>{fmt(datos.fecha_inicio)}</p>
              </div>
              <div style={{ flex: 1, height: '2px', background: datos.fecha_entrega_proyecto ? '#534AB7' : 'rgba(83,74,183,0.2)', minWidth: '20px' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: datos.fecha_entrega_proyecto ? '#BA7517' : 'rgba(83,74,183,0.2)', margin: '0 auto 4px' }} />
                <p style={{ color: '#AFA9EC', fontSize: '0.7rem' }}>Entrega</p>
                <p style={{ color: '#fff', fontSize: '0.78rem' }}>{fmt(datos.fecha_entrega_proyecto)}</p>
              </div>
              <div style={{ flex: 1, height: '2px', background: datos.fecha_fin_obra ? '#1D9E75' : 'rgba(83,74,183,0.2)', minWidth: '20px' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: datos.fecha_fin_obra ? '#1D9E75' : 'rgba(83,74,183,0.2)', margin: '0 auto 4px' }} />
                <p style={{ color: '#AFA9EC', fontSize: '0.7rem' }}>Fin obra</p>
                <p style={{ color: '#fff', fontSize: '0.78rem' }}>{fmt(datos.fecha_fin_obra)}</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {durProyecto !== null && (
                <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 14px' }}>
                  <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>DURACIÓN PROYECTO</p>
                  <p style={{ color: '#7B6FE8', fontWeight: '500' }}>{durProyecto} días</p>
                </div>
              )}
              {durObra !== null && (
                <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 14px' }}>
                  <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>DURACIÓN OBRA</p>
                  <p style={{ color: '#7B6FE8', fontWeight: '500' }}>{durObra} días</p>
                </div>
              )}
              {velProyecto !== null && (
                <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 14px', gridColumn: '1 / -1' }}>
                  <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>VELOCIDAD</p>
                  <p style={{ color: '#7B6FE8', fontWeight: '500' }}>{velProyecto} días/m²</p>
                </div>
              )}
            </div>
          </div>

          {/* Reseña — solo si finalizado y admin */}
          {datos.estado === 'finalizado' && esAdmin && (
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Reseña conseguida</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={toggleResenia} style={{
                  width: '48px', height: '26px', borderRadius: '13px', border: 'none',
                  background: datos.resenia ? '#1D9E75' : 'rgba(83,74,183,0.2)',
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                }}>
                  <span style={{
                    position: 'absolute', top: '3px',
                    left: datos.resenia ? '25px' : '3px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', transition: 'left 0.2s',
                  }} />
                </button>
                <span style={{ color: datos.resenia ? '#1D9E75' : '#AFA9EC', fontSize: '0.88rem' }}>
                  {datos.resenia ? 'Reseña conseguida ✓' : 'Sin reseña todavía'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Columna derecha */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {verDatosEconomicos && <>
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Rentabilidad {esEstimado && <span style={{ color: '#BA7517', fontSize: '0.72rem', marginLeft: '4px', fontWeight: '400', textTransform: 'none' }}>calculada sobre importe encargo (estimación)</span>}</h2>
              {baseCalculo > 0 ? (
                <>
                  {/* Barra visual */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>Coste</span>
                      <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>Encargo</span>
                    </div>
                    <div style={{ height: '12px', background: 'rgba(83,74,183,0.2)', borderRadius: '6px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(100, (costeTotal / baseCalculo) * 100)}%`, background: colorMargen(margen), borderRadius: '6px', transition: 'width 0.4s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      [esEstimado ? 'Importe encargo' : 'Importe facturado', fmtEuros(baseCalculo), '#fff'],
                      ['\u2212 Coste personal', costePersonal ? `\u2212${fmtEuros(costePersonal)}` : '\u2014', '#D85A30'],
                      ['\u2212 Otros costes de producci\u00f3n', costesProduccion ? `\u2212${fmtEuros(costesProduccion)}` : '\u2014', '#D85A30'],
                      ['= Resultado', fmtEuros(resultado), resultado >= 0 ? '#1D9E75' : '#D85A30'],
                      ['Margen', fmtPct(margen), colorMargen(margen)],
                    ].map(([label, valor, color]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(83,74,183,0.08)' }}>
                        <span style={{ color: '#AFA9EC', fontSize: '0.82rem' }}>{label}</span>
                        <span style={{ color, fontSize: '0.88rem', fontWeight: '500' }}>{valor}</span>
                      </div>
                    ))}
                    {precioHoraVendida && (
                      <div style={{ marginTop: '8px', padding: '10px 14px', background: '#0e0b24', borderRadius: '8px' }}>
                        <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginBottom: '2px' }}>PRECIO HORA VENDIDA {esEstimado && '(est.)'}</p>
                        <p style={{ color: '#7B6FE8', fontWeight: '500' }}>{fmtEuros(precioHoraVendida)}/h</p>
                      </div>
                    )}
                  </div>
                </>
              ) : costeTotal > 0 ? (
                // Sesión 34: sin importe de encargo se enseñan igualmente los costes, y se avisa de lo que falta
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    ['Coste personal', fmtEuros(costePersonal)],
                    ['Otros costes de producción', costesProduccion ? fmtEuros(costesProduccion) : '\u2014'],
                    ['Coste total', fmtEuros(costeTotal)],
                  ].map(([label, valor]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(83,74,183,0.08)' }}>
                      <span style={{ color: '#AFA9EC', fontSize: '0.82rem' }}>{label}</span>
                      <span style={{ color: '#D85A30', fontSize: '0.88rem', fontWeight: '500' }}>{valor}</span>
                    </div>
                  ))}
                  <p style={{ color: '#BA7517', fontSize: '0.82rem', marginTop: '6px' }}>Falta el importe del encargo: sin él no se pueden calcular el resultado ni el margen.</p>
                </div>
              ) : (
                <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin datos económicos registrados</p>
              )}
              {horasSinTarifa > 0 && (
                <p style={{ color: '#BA7517', fontSize: '0.78rem', marginTop: '10px' }}>
                  {new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(horasSinTarifa)} h de personas sin tarifa en esa fecha no suman coste. Revisa sus tarifas en Configuración.
                </p>
              )}
            </div>

            {/* Horas por empleado */}
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Horas por empleado</h2>
              {Object.keys(horasPorEmpleado).length === 0 ? (
                <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin horas registradas</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      {['Empleado', 'Horas', '%', 'Coste'].map(h => <th key={h} style={{ ...styles.th, padding: '6px 8px' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(horasPorEmpleado).map(([nombre, data]) => (
                      <tr key={nombre} style={{ borderBottom: '1px solid rgba(83,74,183,0.1)' }}>
                        <td style={{ ...styles.td, padding: '8px' }}>{nombre}</td>
                        <td style={{ ...styles.td, padding: '8px' }}>{fmtHorasTxt(data.horas)}</td>
                        <td style={{ ...styles.td, padding: '8px', color: '#AFA9EC' }}>{totalHoras > 0 ? `${Math.round((data.horas / totalHoras) * 100)}%` : '\u2014'}</td>
                        <td style={{ ...styles.td, padding: '8px', color: '#AFA9EC' }}>{data.coste > 0 ? fmtEuros(data.coste) : '\u2014'}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid rgba(83,74,183,0.3)' }}>
                      <td style={{ ...styles.td, padding: '8px', fontWeight: '600' }}>Total</td>
                      <td style={{ ...styles.td, padding: '8px', fontWeight: '600', color: '#7B6FE8' }}>{fmtHorasTxt(totalHoras)}</td>
                      <td style={{ ...styles.td, padding: '8px' }}>100%</td>
                      <td style={{ ...styles.td, padding: '8px', fontWeight: '600', color: '#AFA9EC' }}>{fmtEuros(Object.values(horasPorEmpleado).reduce((s, d) => s + d.coste, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </>}

          {/* Notas */}
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Notas</h2>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <textarea value={nuevaNota} onChange={e => setNuevaNota(e.target.value)} placeholder="Añadir una nota..." rows={2}
                style={{ ...estiloInput(), resize: 'none', flex: 1 }} />
              <button onClick={añadirNota} disabled={guardandoNota || !nuevaNota.trim()}
                style={{ ...styles.botonPrimario, alignSelf: 'stretch', padding: '0 16px', opacity: !nuevaNota.trim() ? 0.5 : 1 }}>
                {guardandoNota ? '...' : 'Añadir'}
              </button>
            </div>
            {notas.length === 0
              ? <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin notas</p>
              : notas.map(n => (
                <div key={n.id} style={{ background: '#0e0b24', borderRadius: '8px', padding: '10px 14px', borderLeft: '3px solid rgba(83,74,183,0.3)', marginBottom: '8px' }}>
                  <p style={{ color: '#fff', fontSize: '0.88rem', marginBottom: '4px' }}>{n.texto}</p>
                  <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>{fmt(n.fecha)}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
      </div>{/* fin padding wrapper ficha */}
    </div>
  );
}

// ─── MÓDULO PRINCIPAL ─────────────────────────────────────────────────────────

export default function Proyectos({ esAdmin, verDatosEconomicos = true, accionInicial, onAccionCompletada, onNavegar }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { sectores } = useListasConfig(['sectores']);
  // Sesión 32 · Entrega B: proyectos y clientes vienen del almacén compartido (una descarga por sesión).
  // Los documentos se guardan crudos; el nombre del cliente y el margen se derivan aquí.
  const { items: proyCrudos, setItems: setProyectos, cargando: cargandoProy } = useColeccion('proyectos');
  const { items: clientesAlmacen, cargando: cargandoCli } = useColeccion('clientes');
  const cargando = cargandoProy || cargandoCli;
  const proyectos = useMemo(() => {
    const cliMap = {};
    clientesAlmacen.forEach(c => { cliMap[c.id] = c; });
    return proyCrudos.map(p => {
      const cli = cliMap[p.clienteId];
      const clienteNombre = cli
        ? (cli.tipo === 'empresa' ? cli.razon_social : `${cli.nombre || ''} ${cli.apellidos || ''}`.trim())
        : '';
      return { ...p, clienteNombre, margenCalc: calcularMargen(p) };
    });
  }, [proyCrudos, clientesAlmacen]);
  // Sesión 34: si se salió del módulo con un borrador sin guardar, se vuelve a él
  const borradorPendiente = useBorradorPendiente('proyecto');
  const [vista, setVista] = useState(borradorPendiente === 'nuevo' && !accionInicial ? 'nuevo' : 'listado');
  const [proyectoActivo, setProyectoActivo] = useState(null);
  const borradorAbierto = useRef(false);
  useEffect(() => {
    if (borradorAbierto.current || !borradorPendiente || borradorPendiente === 'nuevo' || cargando || accionInicial) return;
    borradorAbierto.current = true;
    const p = proyectos.find(x => x.id === borradorPendiente);
    if (p) { setProyectoActivo(p); setVista('ficha'); }
  }, [borradorPendiente, cargando, proyectos, accionInicial]);
  const [presupuestoIdInicial, setPresupuestoIdInicial] = useState(null);
  // Sesión 34: de dónde se abrió el alta (p. ej. 'presupuestos'), para volver allí al cancelar o guardar
  const [origenAlta, setOrigenAlta] = useState(null);
  const parchearPresupuestos = useParchear('presupuestos');
  const descartarBorrador = useDescartarBorrador();

  // Vista, filtros, orden, filas visibles y scroll se recuerdan durante la sesión
  const [vistaKanban, setVistaKanban] = useEstadoPersistente('proyectos.vistaKanban', false);
  const [busqueda, setBusqueda] = useEstadoPersistente('proyectos.busqueda', '');
  const [filtroEstados, setFiltroEstados] = useEstadoPersistente('proyectos.filtroEstados', []);
  const [filtroSector, setFiltroSector] = useEstadoPersistente('proyectos.filtroSector', '');
  const [filtroAnio, setFiltroAnio] = useEstadoPersistente('proyectos.filtroAnio', '');
  const [filtroCliente, setFiltroCliente] = useEstadoPersistente('proyectos.filtroCliente', '');
  const [filtroSupMin, setFiltroSupMin] = useEstadoPersistente('proyectos.filtroSupMin', '');
  const [filtroSupMax, setFiltroSupMax] = useEstadoPersistente('proyectos.filtroSupMax', '');
  const [filtroImpMin, setFiltroImpMin] = useEstadoPersistente('proyectos.filtroImpMin', '');
  const [filtroImpMax, setFiltroImpMax] = useEstadoPersistente('proyectos.filtroImpMax', '');
  const [ordenacion, setOrdenacion] = useEstadoPersistente('proyectos.ordenacion', 'fecha_desc');
  const [visibles, setVisibles] = useEstadoPersistente('proyectos.visibles', 50);
  usePosicionScroll('proyectos', vista === 'listado' && !cargando);

  useAlCambiar(() => setVisibles(50), [busqueda, filtroEstados, filtroSector, filtroAnio, filtroCliente, filtroSupMin, filtroSupMax, filtroImpMin, filtroImpMax, ordenacion]);

  useEffect(() => {
    if (!accionInicial) return;
    if (accionInicial.accion === 'nuevo') {
      // Sesión 34: viniendo de un presupuesto se empieza limpio (un borrador anterior de alta no se mezcla)
      if (accionInicial.presupuestoId) descartarBorrador('proyecto:nuevo');
      setPresupuestoIdInicial(accionInicial.presupuestoId || null);
      setOrigenAlta(accionInicial.origen || null);
      setVista('nuevo');
      if (onAccionCompletada) onAccionCompletada();
    } else if (accionInicial.accion === 'ficha' && accionInicial.id) {
      if (cargando) return; // Sesión 32: esperar a tener los datos; antes la acción se descartaba con la lista aún vacía
      const p = proyectos.find(x => x.id === accionInicial.id);
      if (p) { setProyectoActivo(p); setVista('ficha'); }
      if (onAccionCompletada) onAccionCompletada();
    }
  }, [accionInicial, proyectos, cargando]);

  const handleNuevo = async (form) => {
    const docRef = await addDoc(collection(db, 'proyectos'), { ...form, creado_en: serverTimestamp() });
    // getDoc para obtener timestamps reales — REGLA PERMANENTE
    const snap = await getDoc(docRef);
    const nuevo = { id: snap.id, ...snap.data() };
    sincronizarListaProyecto(snap.id, nuevo);
    // Sesión 34: el presupuesto queda vinculado a su proyecto (su botón pasa a "Ver proyecto")
    if (form.presupuestoId) await vincularPresupuesto(form.presupuestoId, snap.id, parchearPresupuestos);
    setProyectos(prev => [nuevo, ...prev]);
    setPresupuestoIdInicial(null);
    setOrigenAlta(null);
    setProyectoActivo(nuevo);
    setVista('ficha');
  };

  const cancelarAlta = () => {
    setPresupuestoIdInicial(null);
    const origen = origenAlta;
    setOrigenAlta(null);
    if (origen && onNavegar) { setVista('listado'); onNavegar({ modulo: origen }); }
    else setVista('listado');
  };

  const onEliminarProyecto = (id) => {
    setProyectos(prev => prev.filter(p => p.id !== id));
    setVista('listado');
  };

  const handleActualizar = (actualizado) => {
    // Las fechas en memoria son Timestamp, como en Firestore (filtro por año y orden dependen de ello)
    setProyectos(prev => prev.map(p => p.id === actualizado.id ? fechasATimestamp(actualizado) : p));
    setProyectoActivo(actualizado);
  };

  // Filtrado y ordenación
  const aniosDisponibles = [...new Set(proyectos.map(p => {
    const f = p.fecha_inicio?.toDate ? p.fecha_inicio.toDate() : null;
    return f ? f.getFullYear() : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  const clientesDisponibles = [...new Map(proyectos.filter(p => p.clienteId && p.clienteNombre).map(p => [p.clienteId, p.clienteNombre])).entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const proyectosFiltrados = proyectos.filter(p => {
    const texto = norm(busqueda);
    if (busqueda && !(
      norm(String(p.codigo || '')).includes(texto) ||
      norm(p.nombre || '').includes(texto) ||
      norm(p.clienteNombre || '').includes(texto) ||
      norm(p.sector || '').includes(texto)
    )) return false;
    if (filtroEstados.length > 0 && !filtroEstados.includes(p.estado)) return false;
    if (filtroSector && p.sector !== filtroSector) return false;
    if (filtroCliente && p.clienteId !== filtroCliente) return false;
    if (filtroAnio) {
      const f = p.fecha_inicio?.toDate ? p.fecha_inicio.toDate() : null;
      if (!f || f.getFullYear() !== parseInt(filtroAnio)) return false;
    }
    if (filtroSupMin && (p.superficie || 0) < parseFloat(filtroSupMin)) return false;
    if (filtroSupMax && (p.superficie || 0) > parseFloat(filtroSupMax)) return false;
    if (filtroImpMin && (p.importe_encargo || 0) < parseFloat(filtroImpMin)) return false;
    if (filtroImpMax && (p.importe_encargo || 0) > parseFloat(filtroImpMax)) return false;
    return true;
  }).sort((a, b) => {
    const toTs = (f) => {
      if (!f) return 0;
      if (f?.seconds) return f.seconds;
      if (f instanceof Date) return f.getTime() / 1000;
      if (typeof f === 'string') return new Date(f).getTime() / 1000;
      if (f?.toDate) return f.toDate().getTime() / 1000;
      return 0;
    };
    if (ordenacion === 'fecha_desc') return toTs(b.fecha_inicio) - toTs(a.fecha_inicio);
    if (ordenacion === 'fecha_asc') return toTs(a.fecha_inicio) - toTs(b.fecha_inicio);
    if (ordenacion === 'importe_desc') return (b.importe_encargo || 0) - (a.importe_encargo || 0);
    if (ordenacion === 'importe_asc') return (a.importe_encargo || 0) - (b.importe_encargo || 0);
    if (ordenacion === 'margen_desc') return (b.margenCalc || 0) - (a.margenCalc || 0);
    return 0;
  });

  // KPIs dinámicos
  const hayFiltros = busqueda || filtroEstados.length > 0 || filtroSector || filtroAnio || filtroCliente || filtroSupMin || filtroSupMax || filtroImpMin || filtroImpMax;
  const kpiTotal = proyectosFiltrados.length;
  const kpiActivos = proyectosFiltrados.filter(p => p.estado === 'en_curso' || p.estado === 'entregado').length;
  const kpiVolumen = proyectosFiltrados.reduce((s, p) => s + (p.importe_facturado || p.importe_encargo || 0), 0);
  const kpiResultado = proyectosFiltrados.reduce((s, p) => {
    const base = p.estado === 'finalizado' ? (p.importe_facturado || p.importe_encargo || 0) : (p.importe_encargo || 0);
    const coste = (p.coste_personal || 0) + (p.costes_produccion || 0);
    return s + (base - coste);
  }, 0);
  // Margen ponderado: resultado total / volumen total (no media aritmética)
  const kpiMargen = kpiVolumen > 0 ? (kpiResultado / kpiVolumen) * 100 : null;

  const limpiarFiltros = () => {
    setBusqueda(''); setFiltroEstados([]); setFiltroSector(''); setFiltroAnio('');
    setFiltroCliente(''); setFiltroSupMin(''); setFiltroSupMax(''); setFiltroImpMin(''); setFiltroImpMax('');
  };

  if (vista === 'nuevo') return <FormularioProyecto titulo="Nuevo proyecto" onGuardar={handleNuevo} onCancelar={cancelarAlta} esAdmin={esAdmin} presupuestoIdInicial={presupuestoIdInicial} proyectos={proyCrudos}
    textoVolver={origenAlta === 'presupuestos' ? '← Presupuestos' : '← Proyectos'} claveBorrador="proyecto:nuevo" />;
  if (vista === 'ficha' && proyectoActivo) return (
    <FichaProyecto proyecto={proyectoActivo} onVolver={() => { setVista('listado'); }}
      onActualizar={handleActualizar} esAdmin={esAdmin} verDatosEconomicos={verDatosEconomicos}
      onEliminar={onEliminarProyecto} proyectos={proyCrudos} />
  );

  return (
    <div style={styles.container}>
      {/* Cabecera */}
      <div style={styles.cabecera}>
        <h1 style={styles.titulo}>Proyectos</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <BarraActualizacion colecciones={['proyectos', 'clientes']} />
          <button style={{ ...styles.botonSecundario, fontSize: '0.82rem', padding: '8px 14px' }} onClick={() => setVistaKanban(!vistaKanban)}>
            {vistaKanban ? '☰ Tabla' : '⊞ Kanban'}
          </button>
          {/* Sesión 34: el comercial también crea proyectos (cierra la venta); borrar sigue siendo solo del admin */}
          <button style={styles.botonPrimario} onClick={() => { setPresupuestoIdInicial(null); setOrigenAlta(null); setVista('nuevo'); }}>+ Nuevo proyecto</button>
        </div>
      </div>

      {/* KPIs + filtros + tabla — todo con padding lateral consistente */}
      <div style={{ padding: '24px 32px 32px' }}>

      {/* KPIs */}
      <div style={{ ...styles.gridKpis }}>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Total{hayFiltros ? ' (filtrado)' : ''}</p>
          <p style={styles.kpiValor}>{kpiTotal}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>En activo</p>
          <p style={{ ...styles.kpiValor, color: '#534AB7' }}>{kpiActivos}</p>
        </div>
        {verDatosEconomicos && (
          <>
            <div style={styles.kpiCard}>
              <p style={styles.kpiLabel}>Volumen total</p>
              <p style={{ ...styles.kpiValor, color: '#1D9E75', fontSize: '1.3rem' }}>{fmtEuros(kpiVolumen)}</p>
            </div>
            <div style={styles.kpiCard}>
              <p style={styles.kpiLabel}>Resultado</p>
              <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: kpiResultado >= 0 ? '#1D9E75' : '#D85A30' }}>{fmtEuros(kpiResultado)}</p>
            </div>
            <div style={styles.kpiCard}>
              <p style={styles.kpiLabel}>Margen medio</p>
              <p style={{ ...styles.kpiValor, fontSize: '1.3rem', color: colorMargen(kpiMargen) }}>{fmtPct(kpiMargen)}</p>
            </div>
          </>
        )}
      </div>

      {/* Filtros */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
          <input style={styles.buscador} placeholder="Buscar por código, nombre, cliente o sector..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          <div style={styles.pills}>
            {ESTADOS.map(e => (
              <button key={e.valor} style={{ ...styles.pill, background: filtroEstados.includes(e.valor) ? e.color : 'transparent', color: filtroEstados.includes(e.valor) ? '#fff' : '#AFA9EC', borderColor: filtroEstados.includes(e.valor) ? e.color : 'rgba(83,74,183,0.3)' }}
                onClick={() => setFiltroEstados(prev => prev.includes(e.valor) ? prev.filter(x => x !== e.valor) : [...prev, e.valor])}>{e.etiqueta}</button>
            ))}
          </div>
          <select style={styles.select} value={filtroSector} onChange={e => setFiltroSector(e.target.value)}>
            <option value="">Todos los sectores</option>
            {sectores.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={styles.select} value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}>
            <option value="">Todos los años</option>
            {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select style={styles.select} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
            <option value="">Todos los clientes</option>
            {clientesDisponibles.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
          <select style={styles.select} value={ordenacion} onChange={e => setOrdenacion(e.target.value)}>
            <option value="fecha_desc">Más recientes</option>
            <option value="fecha_asc">Más antiguos</option>
            <option value="importe_desc">Mayor importe</option>
            <option value="importe_asc">Menor importe</option>
            {verDatosEconomicos && <option value="margen_desc">Mayor margen</option>}
          </select>
        </div>
        {/* Filtros de rango */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <span style={{ color: '#AFA9EC', fontSize: '0.78rem', flexShrink: 0 }}>Superficie:</span>
          <input style={{ ...styles.buscador, maxWidth: '100px', flex: 'none', colorScheme: 'dark' }} placeholder="De m²" value={filtroSupMin} onChange={e => setFiltroSupMin(e.target.value)} type="number" />
          <input style={{ ...styles.buscador, maxWidth: '100px', flex: 'none', colorScheme: 'dark' }} placeholder="Hasta m²" value={filtroSupMax} onChange={e => setFiltroSupMax(e.target.value)} type="number" />
          <span style={{ color: '#AFA9EC', fontSize: '0.78rem', flexShrink: 0, marginLeft: '8px' }}>Importe:</span>
          <input style={{ ...styles.buscador, maxWidth: '120px', flex: 'none', colorScheme: 'dark' }} placeholder="De €" value={filtroImpMin} onChange={e => setFiltroImpMin(e.target.value)} type="number" />
          <input style={{ ...styles.buscador, maxWidth: '120px', flex: 'none', colorScheme: 'dark' }} placeholder="Hasta €" value={filtroImpMax} onChange={e => setFiltroImpMax(e.target.value)} type="number" />
          {hayFiltros && <button style={styles.botonLimpiar} onClick={limpiarFiltros}>Limpiar filtros</button>}
        </div>

        {/* Leyenda de colores de margen (solo quien ve márgenes) */}
        {verDatosEconomicos && <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
          <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>Margen:</span>
          {[['#1D9E75', '≥ 50% — Bueno'], ['#BA7517', '35–49% — Atención'], ['#D85A30', '< 35% — Requiere acción']].map(([color, label]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>{label}</span>
            </div>
          ))}
          <span style={{ color: '#BA7517', fontSize: '0.75rem' }}>· est. = estimación (proyecto en curso o entregado)</span>
        </div>}
      </div>

      {cargando ? (
        <p style={{ color: '#AFA9EC', padding: '32px 0' }}>Cargando...</p>
      ) : proyectosFiltrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>
          {proyectos.length === 0 ? 'Aún no hay proyectos registrados.' : 'No hay proyectos que coincidan con los filtros.'}
        </div>
      ) : vistaKanban ? (
        <VistaKanban proyectos={proyectosFiltrados} onAbrirFicha={p => { setProyectoActivo(p); setVista('ficha'); }} verDatosEconomicos={verDatosEconomicos} />
      ) : (
        <>
          <VistaTabla proyectos={proyectosFiltrados.slice(0, visibles)} onAbrirFicha={p => { setProyectoActivo(p); setVista('ficha'); }} verDatosEconomicos={verDatosEconomicos} />
          {proyectosFiltrados.length > visibles && (
            <div style={{ textAlign: 'center', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <button style={{ ...styles.botonSecundario, padding: '10px 32px' }} onClick={() => setVisibles(v => v + 50)}>
                Mostrar 50 más
              </button>
              <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>
                Mostrando {Math.min(visibles, proyectosFiltrados.length)} de {proyectosFiltrados.length} — usa el buscador para filtrar
              </span>
            </div>
          )}
        </>
      )}
      </div>{/* fin padding wrapper */}
    </div>
  );
}

// ─── VISTA TABLA ──────────────────────────────────────────────────────────────

function VistaTabla({ proyectos, onAbrirFicha, verDatosEconomicos = true }) {
  const esEstimado = (p) => p.estado === 'en_curso' || p.estado === 'entregado';
  const colBase = ['Código', 'Nombre', 'Cliente', 'Sector', 'Sup. m\u00b2', 'Estado', 'Inicio', 'Entrega', 'Fin obra'];
  // Sesión 34: el importe del encargo (venta) lo ve también el comercial; el margen, solo el administrador
  const columnas = verDatosEconomicos ? [...colBase, 'Encargo', 'Margen'] : [...colBase, 'Encargo'];
  return (
    <div style={styles.tablaWrapper}>
      <table style={styles.tabla}>
        <thead>
          <tr>
            {columnas.map(h => (
              <th key={h} style={styles.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {proyectos.map(p => (
            <tr key={p.id} style={styles.tr} onClick={() => onAbrirFicha(p)}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...styles.td, color: '#7B6FE8', fontWeight: '600' }}>{p.codigo || '—'}</td>
              <td style={styles.td}><span style={styles.nombreLead}>{p.nombre || '—'}</span></td>
              <td style={{ ...styles.td, color: '#AFA9EC' }}>{p.clienteNombre || '—'}</td>
              <td style={{ ...styles.td, color: '#AFA9EC' }}>{p.sector || '—'}</td>
              <td style={{ ...styles.td, color: '#AFA9EC' }}>{p.superficie ? `${fmtNum(p.superficie)} m\u00b2` : '—'}</td>
              <td style={styles.td}><EstadoBadge estado={p.estado} /></td>
              <td style={{ ...styles.td, color: '#AFA9EC', fontSize: '0.8rem' }}>{fmt(p.fecha_inicio)}</td>
              <td style={{ ...styles.td, color: '#AFA9EC', fontSize: '0.8rem' }}>{fmt(p.fecha_entrega_proyecto)}</td>
              <td style={{ ...styles.td, color: '#AFA9EC', fontSize: '0.8rem' }}>{fmt(p.fecha_fin_obra)}</td>
              <td style={styles.td}>{fmtEuros(p.importe_encargo)}</td>
              {verDatosEconomicos && (
                <>
                  <td style={styles.td}>
                    <span style={{ color: colorMargen(p.margenCalc), fontWeight: '600' }}>
                      {p.margenCalc !== null ? fmtPct(p.margenCalc) : '—'}
                    </span>
                    {p.margenCalc !== null && esEstimado(p) && (
                      <span style={{ color: '#BA7517', fontSize: '0.7rem', marginLeft: '4px' }}>est.</span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── VISTA KANBAN ─────────────────────────────────────────────────────────────

function VistaKanban({ proyectos, onAbrirFicha, verDatosEconomicos = true }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
      {ESTADOS.map(estado => {
        const cols = proyectos.filter(p => p.estado === estado.valor);
        const volumen = cols.reduce((s, p) => s + (p.importe_encargo || 0), 0);
        const volumenCol = cols.reduce((s, p) => s + (p.importe_facturado || p.importe_encargo || 0), 0);
        const resultadoCol = cols.reduce((s, p) => {
          const base = p.estado === 'finalizado' ? (p.importe_facturado || p.importe_encargo || 0) : (p.importe_encargo || 0);
          const coste = (p.coste_personal || 0) + (p.costes_produccion || 0);
          return s + (base - coste);
        }, 0);
        const margenMedio = volumenCol > 0 ? (resultadoCol / volumenCol) * 100 : null;
        return (
          <div key={estado.valor}>
            <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#1a1440', borderRadius: '10px', borderTop: `3px solid ${estado.color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#ffffff', fontWeight: '500', fontSize: '0.88rem' }}>{estado.etiqueta}</span>
                <span style={{ color: estado.color, fontWeight: '600', fontSize: '0.82rem' }}>{cols.length}</span>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                <span style={{ color: '#1D9E75', fontSize: '0.75rem' }}>{fmtEuros(volumen)}</span>
                {verDatosEconomicos && margenMedio !== null && <span style={{ color: colorMargen(margenMedio), fontSize: '0.75rem' }}>Margen: {fmtPct(margenMedio)}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {cols.map(p => (
                <div key={p.id} style={{ background: '#1a1440', borderRadius: '10px', padding: '14px 16px', border: '1px solid rgba(83,74,183,0.2)', cursor: 'pointer', transition: 'border-color 0.15s' }}
                  onClick={() => onAbrirFicha(p)}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#534AB7'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(83,74,183,0.2)'}>
                  <p style={{ color: '#7B6FE8', fontSize: '0.75rem', fontWeight: '600', marginBottom: '2px' }}>{p.codigo}</p>
                  <p style={{ color: '#ffffff', fontSize: '0.88rem', fontWeight: '500', marginBottom: '4px' }}>{p.nombre}</p>
                  {p.clienteNombre && <p style={{ color: '#AFA9EC', fontSize: '0.75rem', marginBottom: '6px' }}>{p.clienteNombre}</p>}
                  {(
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>{fmtEuros(p.importe_encargo)}</span>
                      {verDatosEconomicos && p.margenCalc !== null && (
                        <span style={{ color: colorMargen(p.margenCalc), fontSize: '0.75rem', fontWeight: '600' }}>
                          {fmtPct(p.margenCalc)}{(p.estado === 'en_curso' || p.estado === 'entregado') && <span style={{ color: '#BA7517' }}> est.</span>}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {cols.length === 0 && <p style={{ color: '#AFA9EC', fontSize: '0.82rem', textAlign: 'center', padding: '20px 0' }}>Sin proyectos</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── ESTILOS ──────────────────────────────────────────────────────────────────

const styles = {
  container: { padding: '0', maxWidth: '100%' },
  cabecera: { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '20px 32px 12px', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  titulo: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em' },
  botonPrimario: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVolver: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },
  botonEditar: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  botonLimpiar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  barraGuardar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', padding: '16px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
  gridKpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' },
  kpiCard: { background: '#1a1440', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(83,74,183,0.2)' },
  kpiLabel: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  kpiValor: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300' },
  filtrosBar: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', alignItems: 'center' },
  buscador: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', minWidth: '200px', flex: '1', fontFamily: 'DM Sans, sans-serif' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill: { border: '1px solid', borderRadius: '20px', padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'transparent' },
  select: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#AFA9EC', fontSize: '0.88rem', outline: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  tablaWrapper: { overflowX: 'auto' },
  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  tr: { borderBottom: '1px solid rgba(83,74,183,0.1)', cursor: 'pointer' },
  td: { padding: '12px 14px', color: '#ffffff', fontSize: '0.88rem', verticalAlign: 'middle' },
  nombreLead: { display: 'block', fontWeight: '500' },
  subLead: { display: 'block', color: '#AFA9EC', fontSize: '0.78rem' },
  seccion: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)', marginBottom: '16px' },
  seccionTitulo: { color: '#ffffff', fontSize: '0.9rem', fontWeight: '500', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  label: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' },
};
